/**
 * DealDetailPage — full deal detail with tabs:
 *   Overview · Measurement · Operations · Weekly Reviews · Final Review
 *
 * Also carries the state-machine action buttons (Approve/Schedule/Launch/…).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, XCircle, Play, Pause, StopCircle, Copy,
  Archive, Trash2, Sparkles, Loader2, TrendingUp, User, Calendar as CalIcon,
  Tag, Rocket,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  ai_suggested: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  scheduled: 'bg-blue-100 text-blue-800',
  live: 'bg-pink-100 text-pink-800',
  paused: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-purple-100 text-purple-800',
  rejected: 'bg-red-100 text-red-800',
  archived: 'bg-slate-200 text-slate-700',
};

const PERF_STYLES = {
  overperforming: 'text-emerald-700 bg-emerald-50',
  on_track: 'text-emerald-700 bg-emerald-50',
  needs_attention: 'text-amber-700 bg-amber-50',
  underperforming: 'text-red-700 bg-red-50',
  not_enough_data: 'text-gray-700 bg-gray-50',
};

const fmt = (n) => (n == null || isNaN(n) ? '—' : `£${Number(n).toFixed(2)}`);

export default function DealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [deal, setDeal] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [final, setFinal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [finalOpen, setFinalOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/deals/${id}`);
      setDeal(res.data.deal);
      setWeekly(res.data.weekly_reviews || []);
      setFinal(res.data.final_review || null);
    } catch (e) {
      toast.error('Deal not found');
      navigate('/marketing/deals-calendar');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const runAction = async (path, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/deals/${id}/${path}`, extra);
      toast.success('Done');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed: ${path}`);
    } finally { setBusy(false); }
  };

  const deleteDeal = async () => {
    if (!window.confirm('Delete this deal permanently?')) return;
    setBusy(true);
    try {
      await api.delete(`/deals/${id}`);
      toast.success('Deleted');
      navigate('/marketing/deals-calendar');
    } catch (e) {
      toast.error('Failed to delete');
    } finally { setBusy(false); }
  };

  if (loading || !deal) {
    return <div className="p-10 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;
  }

  const canApprove = ['draft', 'ai_suggested'].includes(deal.status);
  const canSchedule = deal.status === 'approved';
  const canLaunch = ['approved', 'scheduled', 'paused'].includes(deal.status);
  const canPause = deal.status === 'live';
  const canComplete = ['live', 'paused', 'scheduled'].includes(deal.status);
  const canReject = ['ai_suggested', 'draft'].includes(deal.status);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4" data-testid="deal-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/deals-calendar')}>
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="outline">{deal.deal_type?.replace(/_/g, ' ')}</Badge>
                <Badge className={STATUS_STYLES[deal.status]}>{deal.status?.replace(/_/g, ' ')}</Badge>
                {deal.source === 'ai' && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> AI
                  </Badge>
                )}
                {deal.performance_status && (
                  <Badge className={PERF_STYLES[deal.performance_status]}>
                    {deal.performance_status.replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl font-bold" data-testid="deal-name">{deal.name}</h1>
              {deal.goal && <p className="text-sm text-muted-foreground mt-1">{deal.goal}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              {canApprove && (
                <Button size="sm" onClick={() => setApproveOpen(true)} disabled={busy} data-testid="detail-approve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                </Button>
              )}
              {canSchedule && (
                <Button size="sm" onClick={() => runAction('schedule')} disabled={busy} data-testid="detail-schedule-btn">
                  <CalIcon className="w-4 h-4 mr-1" /> Schedule
                </Button>
              )}
              {canLaunch && (
                <Button size="sm" onClick={() => runAction('launch')} disabled={busy} data-testid="detail-launch-btn">
                  <Rocket className="w-4 h-4 mr-1" /> Launch
                </Button>
              )}
              {canPause && (
                <Button size="sm" variant="outline" onClick={() => runAction('pause')} disabled={busy}>
                  <Pause className="w-4 h-4 mr-1" /> Pause
                </Button>
              )}
              {canComplete && (
                <Button size="sm" variant="outline" onClick={() => runAction('complete')} disabled={busy} data-testid="detail-complete-btn">
                  <StopCircle className="w-4 h-4 mr-1" /> Complete
                </Button>
              )}
              {canReject && (
                <Button size="sm" variant="ghost" onClick={() => runAction('reject')} disabled={busy}>
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => runAction('duplicate')} disabled={busy}>
                <Copy className="w-4 h-4 mr-1" /> Duplicate
              </Button>
              <Button size="sm" variant="ghost" onClick={() => runAction('archive')} disabled={busy}>
                <Archive className="w-4 h-4 mr-1" /> Archive
              </Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={deleteDeal} disabled={busy}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground pt-2 border-t">
            {deal.target_customer && (
              <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {deal.target_customer}</span>
            )}
            {(deal.start_date || deal.end_date) && (
              <span className="flex items-center gap-1">
                <CalIcon className="w-3.5 h-3.5" />
                {deal.start_date || '—'} → {deal.end_date || '—'}
              </span>
            )}
            {deal.pos_tracking_name && (
              <span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> {deal.pos_tracking_name}</span>
            )}
            {deal.expected_impact && (
              <span className="flex items-center gap-1 text-emerald-700"><TrendingUp className="w-3.5 h-3.5" /> {deal.expected_impact}</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="measurement" data-testid="tab-measurement">Measurement</TabsTrigger>
          <TabsTrigger value="operations" data-testid="tab-operations">Operations</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly Reviews ({weekly.length})</TabsTrigger>
          <TabsTrigger value="final" data-testid="tab-final">Final Review</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardContent className="p-5 space-y-3">
              <Field label="Secondary goal" value={deal.secondary_goal} />
              <Field label="Target behaviour" value={deal.target_behaviour} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Normal price" value={fmt(deal.normal_price)} />
                <Field label="Deal price" value={fmt(deal.deal_price)} />
                <Field label="Discount" value={deal.discount_value} />
              </div>
              <Field label="Items included" value={(deal.items_included || []).join(', ')} />
              <Field label="Active days" value={(deal.active_days || []).join(', ')} />
              <Field label="Active time windows" value={(deal.active_time_windows || []).join(', ')} />
              <Field label="Channels" value={(deal.channels || []).join(', ')} />
              {deal.ai_reasoning && (
                <div className="p-3 bg-primary/5 rounded border">
                  <div className="text-xs font-medium mb-1 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-primary" /> AI reasoning
                  </div>
                  <p className="text-sm">{deal.ai_reasoning}</p>
                </div>
              )}
              {(deal.assumptions || []).length > 0 && (
                <div>
                  <div className="text-xs font-medium mb-1">Assumptions</div>
                  <ul className="text-sm list-disc list-inside">
                    {deal.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="measurement">
          <Card>
            <CardContent className="p-5 space-y-3">
              <Field label="Success criteria" value={deal.success_criteria} />
              <Field label="Kill criteria" value={deal.kill_criteria} />
              <Field label="Expected impact" value={deal.expected_impact} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Target sales" value={fmt(deal.target_sales)} />
                <Field label="Baseline sales" value={fmt(deal.baseline_sales)} />
                <Field label="Margin %" value={deal.margin_pct != null ? `${deal.margin_pct}%` : '—'} />
              </div>
              <Field label="POS tracking name" value={deal.pos_tracking_name} />
              <Field label="Coupon code" value={deal.coupon_code} />
              <Field label="Risks" value={deal.risks} />
              <Field label="Data used" value={(deal.data_used || []).join(', ')} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="operations">
          <Card>
            <CardContent className="p-5 space-y-3">
              <Field label="Staff instruction" value={deal.staff_instruction} multiline />
              <Field label="POS instruction" value={deal.pos_instruction} multiline />
              <Field label="Customer copy" value={deal.customer_copy} multiline />
              <Field label="Signage copy" value={deal.signage_copy} multiline />
              <Field label="Social copy" value={deal.social_copy} multiline />
              <Field label="Bundle details" value={deal.bundle_details} multiline />
              <Field label="Notes" value={deal.notes} multiline />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="weekly">
          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex justify-between items-center">
                <div className="font-medium">Weekly Reviews</div>
                <Button size="sm" onClick={() => setWeeklyOpen(true)} data-testid="add-weekly-btn">Add Weekly Review</Button>
              </div>
              {weekly.length === 0 ? (
                <p className="text-sm text-muted-foreground">No weekly reviews yet.</p>
              ) : (
                <div className="space-y-2">
                  {weekly.map((w) => (
                    <div key={w.id} className="p-3 border rounded" data-testid={`weekly-${w.id}`}>
                      <div className="flex justify-between mb-1">
                        <div className="font-medium">{w.week_start_date} → {w.week_end_date}</div>
                        <Badge className={PERF_STYLES[w.performance_status] || ''}>
                          {w.performance_status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div><span className="text-muted-foreground">Sales</span>: {fmt(w.sales)}</div>
                        <div><span className="text-muted-foreground">Baseline</span>: {fmt(w.baseline_sales)}</div>
                        <div><span className="text-muted-foreground">Incr.</span>: {fmt(w.incremental_sales)}</div>
                        <div><span className="text-muted-foreground">Incr. GP</span>: {fmt(w.incremental_gross_profit)}</div>
                        <div><span className="text-muted-foreground">Txns</span>: {w.transactions ?? '—'}</div>
                        <div><span className="text-muted-foreground">Redemptions</span>: {w.redemptions ?? '—'}</div>
                        <div><span className="text-muted-foreground">AOV</span>: {fmt(w.aov)}</div>
                      </div>
                      {w.notes && <p className="text-xs text-muted-foreground mt-2">{w.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="final">
          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex justify-between items-center">
                <div className="font-medium">Final Review</div>
                {!final && (
                  <Button size="sm" onClick={() => setFinalOpen(true)} data-testid="add-final-btn">
                    Complete + File Final Review
                  </Button>
                )}
              </div>
              {!final ? (
                <p className="text-sm text-muted-foreground">Not filed yet. Adding one automatically marks the deal as Completed.</p>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="Total sales" value={fmt(final.total_sales)} />
                    <Field label="Baseline" value={fmt(final.baseline_total_sales)} />
                    <Field label="Incr. revenue" value={fmt(final.incremental_revenue)} />
                    <Field label="Incr. GP" value={fmt(final.incremental_gross_profit)} />
                    <Field label="Uplift %" value={final.uplift_pct != null ? `${final.uplift_pct}%` : '—'} />
                    <Field label="Transactions" value={final.total_transactions ?? '—'} />
                    <Field label="Redemptions" value={final.total_redemptions ?? '—'} />
                    <Field label="Complexity" value={final.operational_complexity_rating} />
                  </div>
                  <Field label="Customer response" value={final.customer_response} multiline />
                  <Field label="Staff response" value={final.staff_response} multiline />
                  <Field label="Final recommendation" value={final.final_recommendation} multiline />
                  <Field label="Lessons learned" value={final.lessons_learned} multiline />
                  <Field label="Suggested next deal" value={final.suggested_next_deal} multiline />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ApproveDialog open={approveOpen} onOpenChange={setApproveOpen} deal={deal}
        onConfirm={() => { setApproveOpen(false); runAction('approve'); }} />
      <WeeklyReviewDialog open={weeklyOpen} onOpenChange={setWeeklyOpen} dealId={id}
        onCreated={reload} />
      <FinalReviewDialog open={finalOpen} onOpenChange={setFinalOpen} dealId={id}
        onCreated={reload} deal={deal} />
    </div>
  );
}

function Field({ label, value, multiline }) {
  if (!value && value !== 0) value = '—';
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      {multiline ? (
        <p className="text-sm whitespace-pre-wrap">{value}</p>
      ) : (
        <div className="text-sm font-medium">{value}</div>
      )}
    </div>
  );
}

function ApproveDialog({ open, onOpenChange, deal, onConfirm }) {
  const hasTracking = !!(deal?.pos_tracking_name || deal?.coupon_code);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle>Approve this deal?</DialogTitle>
          <DialogDescription>
            Once approved you can schedule and launch it.
          </DialogDescription>
        </DialogHeader>
        {!hasTracking && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
            No POS tracking name or coupon code set — measurement will be manual only.
            You can add one later.
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm} data-testid="approve-confirm-btn">Approve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WeeklyReviewDialog({ open, onOpenChange, dealId, onCreated }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    week_start_date: '', week_end_date: '', sales: '', transactions: '',
    redemptions: '', baseline_sales: '', staff_feedback: '', customer_feedback: '',
    operational_issues: '', recommendation: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.week_start_date || !form.week_end_date) {
      toast.error('Week dates are required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        sales: form.sales === '' ? null : Number(form.sales),
        transactions: form.transactions === '' ? null : Number(form.transactions),
        redemptions: form.redemptions === '' ? null : Number(form.redemptions),
        baseline_sales: form.baseline_sales === '' ? null : Number(form.baseline_sales),
      };
      await api.post(`/deals/${dealId}/weekly-reviews`, payload);
      toast.success('Weekly review saved');
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="weekly-review-dialog">
        <DialogHeader>
          <DialogTitle>Weekly Review</DialogTitle>
          <DialogDescription>Log what happened this week. Baseline helps measure lift.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Week start</Label>
              <Input type="date" value={form.week_start_date} onChange={(e) => setField('week_start_date', e.target.value)} data-testid="wr-start" />
            </div>
            <div>
              <Label>Week end</Label>
              <Input type="date" value={form.week_end_date} onChange={(e) => setField('week_end_date', e.target.value)} data-testid="wr-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sales (£)</Label>
              <Input type="number" step="0.01" value={form.sales} onChange={(e) => setField('sales', e.target.value)} data-testid="wr-sales" />
            </div>
            <div>
              <Label>Baseline sales (£)</Label>
              <Input type="number" step="0.01" value={form.baseline_sales} onChange={(e) => setField('baseline_sales', e.target.value)} data-testid="wr-baseline" />
            </div>
            <div>
              <Label>Transactions</Label>
              <Input type="number" value={form.transactions} onChange={(e) => setField('transactions', e.target.value)} />
            </div>
            <div>
              <Label>Redemptions</Label>
              <Input type="number" value={form.redemptions} onChange={(e) => setField('redemptions', e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Staff feedback</Label>
            <Textarea rows={2} value={form.staff_feedback} onChange={(e) => setField('staff_feedback', e.target.value)} />
          </div>
          <div>
            <Label>Customer feedback</Label>
            <Textarea rows={2} value={form.customer_feedback} onChange={(e) => setField('customer_feedback', e.target.value)} />
          </div>
          <div>
            <Label>Operational issues</Label>
            <Textarea rows={2} value={form.operational_issues} onChange={(e) => setField('operational_issues', e.target.value)} />
          </div>
          <div>
            <Label>Recommendation</Label>
            <Textarea rows={2} value={form.recommendation} onChange={(e) => setField('recommendation', e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="wr-save-btn">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FinalReviewDialog({ open, onOpenChange, dealId, onCreated, deal }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    total_sales: '', total_redemptions: '', total_transactions: '',
    baseline_total_sales: '', margin_pct: deal?.margin_pct ?? '',
    operational_complexity_rating: deal?.operational_complexity || '',
    customer_response: '', staff_response: '', final_recommendation: '',
    lessons_learned: '', suggested_next_deal: '',
  });
  const [busy, setBusy] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        total_sales: form.total_sales === '' ? null : Number(form.total_sales),
        total_redemptions: form.total_redemptions === '' ? null : Number(form.total_redemptions),
        total_transactions: form.total_transactions === '' ? null : Number(form.total_transactions),
        baseline_total_sales: form.baseline_total_sales === '' ? null : Number(form.baseline_total_sales),
        margin_pct: form.margin_pct === '' ? null : Number(form.margin_pct),
      };
      await api.post(`/deals/${dealId}/final-review`, payload);
      toast.success('Final review filed. Deal marked as Completed.');
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="final-review-dialog">
        <DialogHeader>
          <DialogTitle>Final Review</DialogTitle>
          <DialogDescription>
            Filing this marks the deal as Completed and adds it to your Deal Library.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Total sales (£)</Label>
              <Input type="number" step="0.01" value={form.total_sales} onChange={(e) => setField('total_sales', e.target.value)} data-testid="fr-total-sales" />
            </div>
            <div>
              <Label>Baseline total (£)</Label>
              <Input type="number" step="0.01" value={form.baseline_total_sales} onChange={(e) => setField('baseline_total_sales', e.target.value)} data-testid="fr-baseline" />
            </div>
            <div>
              <Label>Total transactions</Label>
              <Input type="number" value={form.total_transactions} onChange={(e) => setField('total_transactions', e.target.value)} />
            </div>
            <div>
              <Label>Redemptions</Label>
              <Input type="number" value={form.total_redemptions} onChange={(e) => setField('total_redemptions', e.target.value)} />
            </div>
            <div>
              <Label>Margin %</Label>
              <Input type="number" step="0.1" value={form.margin_pct} onChange={(e) => setField('margin_pct', e.target.value)} />
            </div>
            <div>
              <Label>Complexity</Label>
              <Input value={form.operational_complexity_rating} onChange={(e) => setField('operational_complexity_rating', e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Customer response</Label>
            <Textarea rows={2} value={form.customer_response} onChange={(e) => setField('customer_response', e.target.value)} />
          </div>
          <div>
            <Label>Staff response</Label>
            <Textarea rows={2} value={form.staff_response} onChange={(e) => setField('staff_response', e.target.value)} />
          </div>
          <div>
            <Label>Final recommendation</Label>
            <Textarea rows={2} value={form.final_recommendation} onChange={(e) => setField('final_recommendation', e.target.value)} />
          </div>
          <div>
            <Label>Lessons learned</Label>
            <Textarea rows={2} value={form.lessons_learned} onChange={(e) => setField('lessons_learned', e.target.value)} />
          </div>
          <div>
            <Label>Suggested next deal</Label>
            <Textarea rows={2} value={form.suggested_next_deal} onChange={(e) => setField('suggested_next_deal', e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="fr-save-btn">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save & Complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
